import type {
  Building,
  Equipment,
  Finding,
  OperationalLoopReport,
  Recipe,
  Zone,
} from "@/lib/api/building";
import {
  getBuilding,
  getOperationalLoop,
  listEquipment,
  listFindingsForScope,
  listRecipes,
  listZones,
} from "@/lib/api/building";
import type {
  KBEvaluationRun,
  KBTickSummary,
  KBTypesResponse,
} from "@/lib/api/agent";
import {
  getKBEvaluationRun,
  getKBTypes,
  listKBTickSummaries,
} from "@/lib/api/agent";

/** Composed live Energiatehokkuus bag for one building — parity with OptimizationBuildingSection. */
export type EnergiatehokkuusBuildingSnapshotBag = {
  building: Building | null;
  recipes: Recipe[];
  equipment: Equipment[];
  equipmentTypeLabels: Record<string, string>;
  zones: Zone[];
  opportunityFindings: Finding[];
  kbEvaluationRun: KBEvaluationRun | null;
  kbTickSummaries: KBTickSummary[];
  operationalLoopReport: OperationalLoopReport | null;
};

/** Injected HTTP ports for composing an Energiatehokkuus building snapshot. */
export type EnergiatehokkuusBuildingSnapshotDeps = {
  getBuilding: (buildingId: string) => Promise<Building | null>;
  listRecipes: (buildingId: string) => Promise<Recipe[]>;
  listEquipment: (buildingId: string) => Promise<Equipment[]>;
  listZones: (buildingId: string) => Promise<Zone[]>;
  getKBTypes: () => Promise<KBTypesResponse>;
  listOpportunityFindings: (buildingId: string) => Promise<Finding[]>;
  getKBEvaluationRun: (buildingId: string) => Promise<KBEvaluationRun | null>;
  listKBTickSummaries: (
    buildingId: string,
  ) => Promise<{ building_id: string; ticks: KBTickSummary[] }>;
};

const defaultSnapshotDeps: EnergiatehokkuusBuildingSnapshotDeps = {
  getBuilding,
  listRecipes,
  listEquipment,
  listZones,
  getKBTypes,
  listOpportunityFindings: (buildingId) =>
    listFindingsForScope(
      { building: buildingId },
      { category: "opportunity" },
    ),
  getKBEvaluationRun,
  listKBTickSummaries,
};

export type FetchEnergiatehokkuusBuildingSnapshot = (
  buildingId: string,
) => Promise<EnergiatehokkuusBuildingSnapshotBag>;

export type FetchEnergiatehokkuusOperationalLoop = (
  buildingId: string,
  equipmentId: string,
) => Promise<OperationalLoopReport | null>;

export type EnergiatehokkuusBuildingSnapshotState =
  | { status: "idle" }
  | { status: "loading" }
  | {
      status: "ok";
      buildingId: string;
      bag: EnergiatehokkuusBuildingSnapshotBag;
    };

export interface EnergiatehokkuusBuildingSnapshotCache {
  getState: (buildingId: string) => EnergiatehokkuusBuildingSnapshotState;
  /** First load or join in-flight load; no-op HTTP when that building is already ok. */
  load: (buildingId: string) => Promise<void>;
  /**
   * Reload only the operational-loop report for the UI-selected equipment.
   * Does not refetch building-level fields. Ensures building `load` first when needed.
   */
  loadOperationalLoop: (
    buildingId: string,
    equipmentId: string,
  ) => Promise<void>;
  /**
   * Patch the cached building after Live optimization toggle (or similar).
   * No HTTP — callers PATCH first, then apply the response here.
   */
  patchBuilding: (buildingId: string, building: Building) => void;
  subscribe: (
    buildingId: string,
    listener: () => void,
  ) => () => void;
}

type BuildingEntry = {
  state: EnergiatehokkuusBuildingSnapshotState;
  inFlight: Promise<void> | null;
  loopInFlight: Promise<void> | null;
  loopInFlightEquipmentId: string | null;
  listeners: Set<() => void>;
};

export function emptyEnergiatehokkuusBuildingSnapshotBag(): EnergiatehokkuusBuildingSnapshotBag {
  return {
    building: null,
    recipes: [],
    equipment: [],
    equipmentTypeLabels: {},
    zones: [],
    opportunityFindings: [],
    kbEvaluationRun: null,
    kbTickSummaries: [],
    operationalLoopReport: null,
  };
}

/**
 * Compose the building-level Energiatehokkuus bag (no operational-loop report yet).
 * Each underlying fetch soft-fails — parity with OptimizationBuildingSection effects.
 */
export async function fetchEnergiatehokkuusBuildingSnapshotBag(
  buildingId: string,
  deps: EnergiatehokkuusBuildingSnapshotDeps = defaultSnapshotDeps,
): Promise<EnergiatehokkuusBuildingSnapshotBag> {
  const [
    building,
    recipes,
    equipment,
    zones,
    kbTypes,
    opportunityFindings,
    kbEvaluationRun,
    tickList,
  ] = await Promise.all([
    deps.getBuilding(buildingId).catch((): Building | null => null),
    deps.listRecipes(buildingId).catch((): Recipe[] => []),
    deps.listEquipment(buildingId).catch((): Equipment[] => []),
    deps.listZones(buildingId).catch((): Zone[] => []),
    deps.getKBTypes().catch((): KBTypesResponse => ({
      equipment_types: [],
      zone_types: [],
      services: [],
      sensor_types: [],
    })),
    deps.listOpportunityFindings(buildingId).catch((): Finding[] => []),
    deps
      .getKBEvaluationRun(buildingId)
      .catch((): KBEvaluationRun | null => null),
    deps.listKBTickSummaries(buildingId).catch(() => ({
      building_id: buildingId,
      ticks: [] as KBTickSummary[],
    })),
  ]);

  return {
    building,
    recipes,
    equipment,
    equipmentTypeLabels: Object.fromEntries(
      kbTypes.equipment_types.map((t) => [t.equipment_type, t.name_fi]),
    ),
    zones,
    opportunityFindings,
    kbEvaluationRun,
    kbTickSummaries: tickList.ticks,
    operationalLoopReport: null,
  };
}

/**
 * Framework-agnostic in-memory cache for a building's Energiatehokkuus snapshot.
 * React / Zustand / WebSocket stay outside. No full refresh verb this pass.
 */
export function createEnergiatehokkuusBuildingSnapshotCache(
  fetchSnapshot: FetchEnergiatehokkuusBuildingSnapshot,
  fetchOperationalLoop: FetchEnergiatehokkuusOperationalLoop,
): EnergiatehokkuusBuildingSnapshotCache {
  const entries = new Map<string, BuildingEntry>();

  function entryFor(buildingId: string): BuildingEntry {
    let entry = entries.get(buildingId);
    if (!entry) {
      entry = {
        state: { status: "idle" },
        inFlight: null,
        loopInFlight: null,
        loopInFlightEquipmentId: null,
        listeners: new Set(),
      };
      entries.set(buildingId, entry);
    }
    return entry;
  }

  function notify(entry: BuildingEntry) {
    for (const listener of entry.listeners) listener();
  }

  function setState(
    entry: BuildingEntry,
    next: EnergiatehokkuusBuildingSnapshotState,
  ) {
    entry.state = next;
    notify(entry);
  }

  function runFetch(buildingId: string, entry: BuildingEntry): Promise<void> {
    if (entry.inFlight) return entry.inFlight;
    setState(entry, { status: "loading" });
    entry.inFlight = (async () => {
      try {
        const bag = await fetchSnapshot(buildingId);
        setState(entry, { status: "ok", buildingId, bag });
      } catch {
        setState(entry, {
          status: "ok",
          buildingId,
          bag: emptyEnergiatehokkuusBuildingSnapshotBag(),
        });
      } finally {
        entry.inFlight = null;
      }
    })();
    return entry.inFlight;
  }

  function load(buildingId: string): Promise<void> {
    const entry = entryFor(buildingId);
    if (entry.state.status === "ok") return Promise.resolve();
    return runFetch(buildingId, entry);
  }

  return {
    getState: (buildingId) => entryFor(buildingId).state,
    load,
    loadOperationalLoop: (buildingId, equipmentId) => {
      const entry = entryFor(buildingId);
      if (
        entry.loopInFlight &&
        entry.loopInFlightEquipmentId === equipmentId
      ) {
        return entry.loopInFlight;
      }
      const prior = entry.loopInFlight;
      entry.loopInFlightEquipmentId = equipmentId;
      entry.loopInFlight = (async () => {
        try {
          if (prior) await prior.catch(() => {});
          if (entry.state.status !== "ok") {
            await load(buildingId);
          }
          const current = entryFor(buildingId);
          if (current.state.status !== "ok") return;
          try {
            const report = await fetchOperationalLoop(buildingId, equipmentId);
            if (current.state.status !== "ok") return;
            // A newer equipment selection may have started; don't clobber it.
            if (current.loopInFlightEquipmentId !== equipmentId) return;
            setState(current, {
              status: "ok",
              buildingId,
              bag: {
                ...current.state.bag,
                operationalLoopReport: report,
              },
            });
          } catch {
            if (current.state.status !== "ok") return;
            if (current.loopInFlightEquipmentId !== equipmentId) return;
            setState(current, {
              status: "ok",
              buildingId,
              bag: {
                ...current.state.bag,
                operationalLoopReport: null,
              },
            });
          }
        } finally {
          if (entry.loopInFlightEquipmentId === equipmentId) {
            entry.loopInFlight = null;
            entry.loopInFlightEquipmentId = null;
          }
        }
      })();
      return entry.loopInFlight;
    },
    patchBuilding: (buildingId, building) => {
      const entry = entryFor(buildingId);
      if (entry.state.status !== "ok") return;
      setState(entry, {
        status: "ok",
        buildingId,
        bag: {
          ...entry.state.bag,
          building,
        },
      });
    },
    subscribe: (buildingId, listener) => {
      const entry = entryFor(buildingId);
      entry.listeners.add(listener);
      return () => {
        entry.listeners.delete(listener);
      };
    },
  };
}

let sharedEnergiatehokkuusBuildingSnapshotCache: EnergiatehokkuusBuildingSnapshotCache | null =
  null;

/** App-wide Energiatehokkuus building snapshot cache. */
export function getSharedEnergiatehokkuusBuildingSnapshotCache(): EnergiatehokkuusBuildingSnapshotCache {
  if (!sharedEnergiatehokkuusBuildingSnapshotCache) {
    sharedEnergiatehokkuusBuildingSnapshotCache =
      createEnergiatehokkuusBuildingSnapshotCache(
        (buildingId) => fetchEnergiatehokkuusBuildingSnapshotBag(buildingId),
        (buildingId, equipmentId) =>
          getOperationalLoop(buildingId, equipmentId).catch(
            (): OperationalLoopReport | null => null,
          ),
      );
  }
  return sharedEnergiatehokkuusBuildingSnapshotCache;
}
